import { motion } from 'framer-motion';
import { Message } from '../types/types';

interface MessageCloudProps {
  message: Message;
  index: number;
}

// Default colors for message clouds - blue to gray-blue spectrum
const defaultColors = [
  '#3b82f6', // bright blue
  '#60a5fa', // medium blue
  '#93c5fd', // light blue
  '#6366f1', // indigo
  '#818cf8', // light indigo
  '#64748b', // slate gray-blue
  '#475569', // dark slate gray-blue
  '#0ea5e9', // sky blue
];

export const MessageCloud = ({ message, index }: MessageCloudProps) => {
  // Calculate random X offset for cloud positioning (-30% to 30% of container width)
  // Reduced range to prevent cutoff at edges
  const randomX = ((index * 73) % 60) - 30; // Pseudo-random but consistent per index, more shuffled

  // Use message color if available, otherwise use default color based on user ID hash
  const getColor = () => {
    if (message.color) return message.color;
    // Simple hash of user ID to pick consistent color
    const hash = message.from.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return defaultColors[hash % defaultColors.length];
  };

  // Stagger animation delay based on index
  const delay = index * 0.15;

  return (
    <motion.div
      initial={{ opacity: 0, y: 100, x: 0 }}
      animate={{
        opacity: 1,
        y: 0,
        x: `${randomX}%`,
      }}
      transition={{
        duration: 1.2,
        delay,
        type: 'spring',
        damping: 15,
      }}
      whileHover={{
        scale: 1.05,
        rotate: randomX > 0 ? 2 : -2,
      }}
      className="mb-4"
      style={{
        filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))',
      }}
    >
      <div
        className="relative px-6 py-4 rounded-3xl backdrop-blur-md max-w-xs sm:max-w-sm shadow-lg"
        style={{
          backgroundColor: `${getColor()}CC`, // 80% opacity for more solid glass effect
        }}
      >
        <p className="text-sm sm:text-base text-white font-medium break-words drop-shadow-sm">
          {message.text}
        </p>

        {/* Small decorative cloud tail */}
        <div
          className="absolute -bottom-2 left-8 w-4 h-4 rounded-full"
          style={{
            backgroundColor: `${getColor()}CC`,
          }}
        />
        <div
          className="absolute -bottom-4 left-6 w-3 h-3 rounded-full"
          style={{
            backgroundColor: `${getColor()}CC`,
          }}
        />

        {message.timestamp && (
          <p className="text-xs text-white/70 mt-2 text-right drop-shadow-sm">
            {message.timestamp.toLocaleDateString()}
          </p>
        )}
      </div>
    </motion.div>
  );
};
